import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Loader2, Merge, Scissors, Pencil, RefreshCw, Check, X, Building2, Users } from "lucide-react";

export default function MarketsTab() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [splitFor, setSplitFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/markets"); setRows(data.markets || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setSel([]); setEditing(null); setSplitFor(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };
  const migrate = () => run(() => api.post("/admin/markets/migrate"), L("Mercati ricalcolati", "Markets recomputed"));
  const rename = (key) => run(() => api.post("/admin/markets/rename", { market_key: key, label: editLabel }), L("Rinominato", "Renamed"));
  const merge = (target) => run(() => api.post("/admin/markets/merge", { source_keys: sel.filter((k) => k !== target), target_key: target }), L("Mercati uniti", "Markets merged"));
  const split = (key, cell) => run(() => api.post("/admin/markets/split", { market_key: key, cell }), L("Cella separata", "Cell split"));

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div data-testid="markets-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-muted-foreground max-w-2xl">{L("Ogni struttura è assegnata automaticamente a un mercato/micro-zona (coordinate GPS + geohash ~5km + quartiere). La cache Apify è condivisa solo tra strutture dello stesso mercato. Puoi unire, dividere o rinominare i mercati.", "Each structure is auto-assigned to a market/micro-zone (GPS + geohash ~5km + neighbourhood). The Apify cache is shared only within the same market. You can merge, split or rename markets.")}</p>
        <Button size="sm" variant="outline" onClick={migrate} disabled={busy} data-testid="markets-migrate"><RefreshCw className="w-4 h-4 mr-1" />{L("Ricalcola mercati", "Recompute markets")}</Button>
      </div>

      {sel.length >= 2 && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="merge-bar">
          <Merge className="w-4 h-4 text-primary" />
          <span className="text-sm">{L(`${sel.length} mercati selezionati. Unisci in:`, `${sel.length} markets selected. Merge into:`)}</span>
          {sel.map((k) => {
            const r = rows.find((x) => x.market_key === k);
            return <Button key={k} size="sm" variant="outline" disabled={busy} onClick={() => merge(k)} data-testid={`merge-into-${k}`}>{r?.label || k}</Button>;
          })}
          <Button size="sm" variant="ghost" onClick={() => setSel([])}>{L("Annulla", "Cancel")}</Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr>
            <th className="w-8 px-2 py-2"></th>
            <th className="text-left px-3 py-2">{L("Mercato", "Market")}</th>
            <th className="text-left px-3 py-2">{L("Tipo", "Type")}</th>
            <th className="text-left px-3 py-2"><Building2 className="w-4 h-4 inline" /></th>
            <th className="text-left px-3 py-2"><Users className="w-4 h-4 inline" /></th>
            <th className="text-left px-3 py-2">{L("Celle", "Cells")}</th>
            <th className="text-right px-3 py-2">{L("Azioni", "Actions")}</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.market_key}>
                <tr className="border-t border-border hover:bg-accent/40" data-testid={`market-row-${r.market_key}`}>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" checked={sel.includes(r.market_key)} onChange={() => toggle(r.market_key)} data-testid={`market-select-${r.market_key}`} />
                  </td>
                  <td className="px-3 py-2">
                    {editing === r.market_key ? (
                      <span className="flex items-center gap-1">
                        <input className="h-8 rounded border border-border bg-background px-2 text-sm" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} data-testid="market-rename-input" />
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={busy} onClick={() => rename(r.market_key)} data-testid="market-rename-save"><Check className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-medium">{r.label}</span>
                        {r.manual && <span className="text-[10px] uppercase text-primary">{L("manuale", "manual")}</span>}
                      </span>
                    )}
                    <div className="text-[10px] text-muted-foreground font-data ml-5">{r.market_key}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-2 py-0.5 rounded" style={{ background: r.is_microzone ? "#2563eb18" : "#64748b18", color: r.is_microzone ? "#2563eb" : "#64748b" }}>
                      {r.market_key === "c:unknown" ? L("Senza coordinate", "No coordinates") : r.is_microzone ? L("Micro-zona", "Micro-zone") : L("Città intera", "Whole city")}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-data">{r.properties}</td>
                  <td className="px-3 py-2 font-data">{r.competitors}</td>
                  <td className="px-3 py-2 font-data">{r.cells.length}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(r.market_key); setEditLabel(r.label || ""); }} data-testid={`market-rename-${r.market_key}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {r.cells.length > 1 && <Button size="sm" variant="ghost" onClick={() => setSplitFor(splitFor === r.market_key ? null : r.market_key)} data-testid={`market-split-${r.market_key}`}><Scissors className="w-3.5 h-3.5" /></Button>}
                  </td>
                </tr>
                {splitFor === r.market_key && (
                  <tr className="bg-muted/30" data-testid={`split-panel-${r.market_key}`}>
                    <td></td>
                    <td colSpan={6} className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-muted-foreground">{L("Separa una cella in un nuovo mercato:", "Split a cell into a new market:")}</span>
                        {r.cells.map((c) => (
                          <Button key={c} size="sm" variant="outline" disabled={busy} onClick={() => split(r.market_key, c)} data-testid={`split-cell-${c}`}>{c}</Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
